import React, {
  FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { AddGiftModalComponent } from './types';
import Modal from 'components/base/Modal';

import { AddArticleRequestForm } from 'components/pages/articles/addArticleRequestForm';

import { OrderLine, OrderLineChannel } from 'config/apiFunus/generated/data-contracts';
import { useDeference } from 'hooks/useArticles/useDeference';
import { useDeathLocation } from 'hooks/useDeathLocation';
import { Gift } from 'models/Gift';
import { AllowUndefined } from 'utils/AllowUndefined';
import { showErrorToast, showSuccessToast } from 'utils/toasts';
import './index.scss';

export const AddGiftModal: FC<AddGiftModalComponent> = ({
  show, onCancel, recordId, onSuccess, location, city,
}) => {
  const { t } = useTranslation();
  const [deferenceInfo, setDeferenceInfo] = useState<AllowUndefined<OrderLine>>();
  const [payload, setPayload] = useState<OrderLine>();
  const { getDeathLocationByCode } = useDeathLocation(
    city,
  );

  const { createDeference, getDeference, updateDeferenceStatus } = useDeference();

  const selectedLocation = useMemo(
    () => getDeathLocationByCode(location),
    [getDeathLocationByCode, location],
  );

  useEffect(() => {
    if (!show) {
      setDeferenceInfo(undefined);
    }
  }, [show]);

  useEffect(
    () => {
      if (show && recordId && selectedLocation?.articleCode) {
        getDeference(
          recordId,
          selectedLocation?.articleCode || '',
        )
          .then((response) => {
            setDeferenceInfo(response);
            return response;
          })
          .catch((e) => {
            showErrorToast(t(e.message));
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location, recordId, selectedLocation?.articleCode, show, t],
  );

  const onAccept = useCallback(
    async () => {
      if (payload) {
        try {
          await createDeference(payload);
          await updateDeferenceStatus(recordId, Gift.NO);
          showSuccessToast(t('record.gift.added'));
          onSuccess();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          showErrorToast(e.message);
        }
      }
    },
    [createDeference, onSuccess, payload, recordId, t, updateDeferenceStatus],
  );

  return (
    <Modal
      buttons={[
        {
          id: 'accept',
          onClick: () => onAccept(),
          text: t('common.accept'),
        },
        {
          id: 'cancel',
          onClick: () => onCancel(),
          text: t('common.cancel'),
        },
      ]}
      className="add-gift-modal"
      closeOnFocusLost={false}
      show={show}
      showCancel={false}
      showXButton={false}
      title={t('record.gift.title')}
      onHide={onCancel}
    >
      {show && deferenceInfo && (
      <AddArticleRequestForm
        channel={OrderLineChannel.PROCESSING}
        className="add-gift-modal-form"
        erpId={deferenceInfo.erpId}
        id={recordId}
        priceDisabled={false}
        providerDisabled={false}
        showArticleSelector={false}
        showCategorySelector={false}
        showClientSelector={false}
        value={{
          ...deferenceInfo,
          articleDescription: deferenceInfo.articleDescription,
          client: {
            identifier: selectedLocation?.client || '',
          },
          code: deferenceInfo.code,
          dedicatory: deferenceInfo.dedicatory,
          idSupplier: deferenceInfo.idSupplier,
          price: deferenceInfo.price,
          supplier: deferenceInfo.supplier,
        }}
        articleDisabled
        articleTypeDisabled
        clientDisabled
        showArticleDescription
        onChange={(p) => {
          setPayload(p);
        }}
      />
      )}
    </Modal>
  );
};
